import { LIMITS, normalizePhone, isValidPhone, isValidEmail } from './visitorConstants';

// Client-side mirror of the API's Joi rules. It exists to catch mistakes before
// a round-trip, not to replace the server check — a validation failure there
// comes back in a different response shape than every other error, so it is
// worth never triggering one.
// `requireIdProof` mirrors the society's visitor_id_proof_required setting.
// The API accepts a visitor with no ID either way — that setting is stored but
// never read server-side — so this is the only place it takes effect.
export const validateVisitor = (form, { requireIdProof = false } = {}) => {
    const errors = {};

    if (!form.name?.trim()) errors.name = 'Name is required';
    else if (form.name.trim().length < 2) errors.name = 'Name must be at least 2 characters';
    else if (form.name.trim().length > LIMITS.name) errors.name = `Name must be under ${LIMITS.name} characters`;

    if (!form.phone?.trim()) errors.phone = 'Phone number is required';
    else if (!isValidPhone(form.phone)) errors.phone = 'Enter a 10-digit mobile number, or a full number with country code';

    if (form.email?.trim() && !isValidEmail(form.email)) errors.email = 'Enter a valid email address';

    // An ID number with no document type would be unreadable at the gate.
    if (form.id_proof_number?.trim() && !form.id_proof_type) {
        errors.id_proof_type = 'Pick the document type for this number';
    } else if (requireIdProof && !form.id_proof_type) {
        errors.id_proof_type = 'Your society requires an ID proof';
    }
    if (requireIdProof && form.id_proof_type && !form.id_proof_number?.trim()) {
        errors.id_proof_number = 'Your society requires the document number';
    }

    if (form.photo_url?.trim() && !/^https?:\/\//i.test(form.photo_url.trim())) {
        errors.photo_url = 'Enter a full URL starting with http:// or https://';
    }

    return errors;
};

// Optional text fields are sent even when blank so clearing one actually clears
// the stored value — omitting the key would leave the old one in place on a
// partial update. Joi does not strip unknown keys, so the payload is assembled
// from this explicit list rather than by spreading form state.
const OPTIONAL_TEXT = ['email', 'company_name', 'id_proof_number', 'photo_url', 'address', 'notes'];

export const buildVisitorPayload = (form) => {
    const payload = {
        name: form.name.trim(),
        // Stored verbatim by the API and unique per society, so 9876543210 and
        // +919876543210 would otherwise become two different people.
        phone: normalizePhone(form.phone),
        visitor_type: form.visitor_type || 'GUEST',
    };

    OPTIONAL_TEXT.forEach(k => { payload[k] = (form[k] || '').trim(); });

    // An empty string is not a valid enum value — null is how the field is unset.
    payload.id_proof_type = form.id_proof_type || null;
    if (!payload.id_proof_type) payload.id_proof_number = '';

    return payload;
};
